import React, { useState, useEffect } from 'react';
import authService, { AuthenticationResultStatus } from './AuthorizeService';
import { QueryParameterNames, LogoutActions, ApplicationPaths } from './ApiAuthorizationConstants';
import { Box, CircularProgress, Typography } from '@mui/material';

export const Logout = ({ action }) => {
    const [message, setMessage] = useState(undefined);
    const [isReady, setIsReady] = useState(false);

    const logout = async (returnUrl) => {
        const state = { returnUrl };
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            const result = await authService.signOut(state);
            switch (result.status) {
                case AuthenticationResultStatus.Redirect:
                    // The OIDC client will handle the redirection
                    break;
                case AuthenticationResultStatus.Success:
                    navigateToReturnUrl(returnUrl);
                    break;
                case AuthenticationResultStatus.Fail:
                    setMessage(result.message);
                    break;
                default:
                    throw new Error("Invalid authentication result status.");
            }
        } else {
            setMessage("You successfully logged out!");
        }
    };

    const processLogoutCallback = async () => {
        const url = window.location.href;
        const result = await authService.completeSignOut(url);
        switch (result.status) {
            case AuthenticationResultStatus.Redirect:
                throw new Error('Should not redirect.');
            case AuthenticationResultStatus.Success:
                navigateToReturnUrl(getReturnUrl(result.state));
                break;
            case AuthenticationResultStatus.Fail:
                setMessage(result.message);
                break;
            default:
                throw new Error("Invalid authentication result status.");
        }
    };

    const getReturnUrl = (state) => {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = params.get(QueryParameterNames.ReturnUrl);
        if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
            throw new Error("Invalid return url. The return url needs to have the same origin as the current page.");
        }
        return (state && state.returnUrl) || fromQuery || `${window.location.origin}${ApplicationPaths.LoggedOut}`;
    };

    const navigateToReturnUrl = (returnUrl) => {
        window.location.replace(returnUrl);
    };

    useEffect(() => {
        const processLogout = async () => {
            switch (action) {
                case LogoutActions.Logout:
                    if (window.history.state?.usr?.local) {
                        await logout(getReturnUrl());
                    } else {
                        setMessage("The logout was not initiated from within the page.");
                    }
                    break;
                case LogoutActions.LogoutCallback:
                    await processLogoutCallback();
                    break;
                case LogoutActions.LoggedOut:
                    setMessage("You successfully logged out!");
                    break;
                default:
                    throw new Error(`Invalid action '${action}'`);
            }
            setIsReady(true);
        };

        processLogout();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action]);

    if (!isReady) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const renderMessage = () => {
        const messageMap = {
            [LogoutActions.Logout]: "Processing logout...",
            [LogoutActions.LogoutCallback]: "Processing logout callback...",
            [LogoutActions.LoggedOut]: "You have successfully logged out.",
        };

        return message || messageMap[action] || "Processing...";
    };

    return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6">{renderMessage()}</Typography>
        </Box>
    );
};
